import React from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import type {ProyectoFormDTO} from '../../../../api/Objetos';
import {certificacionesObraApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {JsonTableField, ToggleField} from '../../../../ui/form/fields';
import {FormSection, NestedPanel, useProjectReadOnly, useProjectPublicView} from '../projectForm.helpers';
import {SectionAlerts, SectionSaveButton} from '../sectionCrudUtils';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';
import {useQuery} from "@tanstack/react-query";
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';
import {DocumentosAdjuntosPanel} from '../DocumentosAdjuntosPanel';

function CertificacionesSectionFields(props: { proyectoId: number; projectSlug: string; showIds?: boolean, isLoading: boolean, loadError: any }) {
    const publicView = useProjectPublicView();
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const readOnly = useProjectReadOnly();
    return (
        <FormSection
            id="certificaciones"
            title={t('certificacionesDeObraSection.title')}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <ToggleField
                        label={t('certificacionesDeObraSection.public')}
                        name="certificacionesObra.publico"
                        register={form.register}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            <JsonTableField
                label={t('certificacionesDeObraSection.workCertification')}
                name="certificacionesObra.datosVariables"
                control={form.control}
                projectId={props.proyectoId}
                projectSlug={props.projectSlug}
                slug="certificaciones-obra-datos-variables"
                previewMode="link"
            />
            {!publicView ? (
                <NestedPanel title={t('documentosAdjuntos.certificationDocumentation')}>
                    <DocumentosAdjuntosPanel proyectoId={props.proyectoId} ambito="CERTIFICACIONES_OBRA"/>
                </NestedPanel>
            ) : null}
        </FormSection>
    );
}

export function CertificacionesDeObraSection(props: { proyectoId: number; projectSlug: string; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({defaultValues: createEmptyProyecto()});

    const { data: certData, refetch, isPending, error } = useQuery({
        queryKey: ['admin', 'proyectos', 'certificaciones-obra', props.proyectoId],
        queryFn: () => certificacionesObraApi.get(props.proyectoId),
    });

    React.useEffect(() => {
        if (certData) {
            form.reset({ ...form.getValues(), certificacionesObra: certData });
        }
    }, [certData, form]);

    const handleSave = form.handleSubmit(async (value) => {
        if (value.certificacionesObra) {
            await certificacionesObraApi.save(props.proyectoId, value.certificacionesObra);
            await refetch();
        }
    });

    useRegisterSectionSave({
        id: 'certificaciones',
        isDirty: form.formState.isDirty,
        isSaving: isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <CertificacionesSectionFields 
                proyectoId={props.proyectoId} 
                projectSlug={props.projectSlug}
                showIds={props.showIds}
                isLoading={isPending}
                loadError={error}
            />
            {!readOnly && <SectionSaveButton onClick={() => void handleSave()} busy={isPending} />}
        </FormProvider>
    );
}
